import type { AssociatedObject } from "./types";

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  if (value && typeof value === "object" && !Array.isArray(value)) return value as UnknownRecord;
  return null;
}

function asNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(asNonEmptyString)
    .filter((entry): entry is string => typeof entry === "string");
}

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>();
  return values
    .map((value) => value.trim())
    .filter((value) => value.length > 0)
    .filter((value) => {
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
}

export type EmailViewModel = {
  subject: string;
  snippet: string;
  sender: {
    display: string;
    name?: string;
    email?: string;
  };
  recipients: string[];
  timestamp?: string;
  labels: string[];
  direction?: string;
  textBody?: string;
  htmlBody?: string;
};

export type EmailRecordLike = {
  meta_data: Record<string, unknown>;
  created_at?: string | null;
  key_fields_text?: string | null;
  thread_id?: string | null;
};

export function getEmailViewModel(
  email: Pick<AssociatedObject, "meta_data" | "created_at" | "key_fields_text"> | EmailRecordLike,
): EmailViewModel {
  const metadata = asRecord(email.meta_data) ?? {};

  const subject =
    asNonEmptyString(metadata.subject) ??
    asNonEmptyString(metadata.native_subject) ??
    "(No subject)";

  const senderEmail =
    asNonEmptyString(metadata.sender) ??
    asNonEmptyString(metadata.native_sender_email) ??
    asNonEmptyString(metadata.from);

  const senderName =
    asNonEmptyString(metadata.sender_name) ??
    asNonEmptyString(metadata.native_sender_name);

  const senderDisplay = senderName ?? senderEmail ?? "(Unknown sender)";

  const timestamp =
    asNonEmptyString(metadata.timestamp) ??
    asNonEmptyString(metadata.native_sent_at) ??
    asNonEmptyString(metadata.native_created_at_tz) ??
    asNonEmptyString(email.created_at);

  const direction =
    asNonEmptyString(metadata.direction) ?? asNonEmptyString(metadata.native_direction);

  const recipients = uniqueStrings([
    ...asStringArray(metadata.recipients),
    ...asStringArray(metadata.to),
    ...asStringArray(metadata.cc),
    ...asStringArray(metadata.bcc),
    ...asStringArray(metadata.native_access_recipients),
  ]).filter((recipient) => (senderEmail ? recipient !== senderEmail : true));

  const textBody =
    asNonEmptyString(metadata.content) ?? asNonEmptyString(metadata.native_message_text);

  const htmlBody =
    asNonEmptyString(metadata.html) ?? asNonEmptyString(metadata.native_message_html);

  const rawSnippet =
    asNonEmptyString(metadata.native_snippet) ??
    asNonEmptyString(metadata.snippet) ??
    (textBody ? textBody.replace(/\s+/g, " ").trim() : undefined) ??
    asNonEmptyString(email.key_fields_text);

  const snippet = rawSnippet ? rawSnippet.slice(0, 220) : "";

  const labels = uniqueStrings([
    ...asStringArray(metadata.labels),
    ...asStringArray(metadata.native_label_ids),
  ]);

  return {
    subject,
    snippet,
    sender: { display: senderDisplay, email: senderEmail, name: senderName },
    recipients,
    timestamp,
    labels,
    direction,
    textBody,
    htmlBody,
  };
}

export function emailMatchesQuery(
  email: Pick<AssociatedObject, "meta_data" | "created_at" | "key_fields_text">,
  query: string,
): boolean {
  const trimmed = query.trim();
  if (!trimmed) return true;

  const view = getEmailViewModel(email);
  const haystack = [
    view.subject,
    view.sender.display,
    view.sender.email ?? "",
    view.recipients.join(" "),
    view.snippet,
    view.textBody ? view.textBody.slice(0, 2000) : "",
    email.key_fields_text ?? "",
  ]
    .join(" ")
    .toLowerCase();

  return haystack.includes(trimmed.toLowerCase());
}

export function extractHtmlBody(html: string): string {
  try {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    return parsed.body?.innerHTML || html;
  } catch {
    return html;
  }
}

export function humanizeEmailTag(value: string): string {
  return value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
